import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, MapPin, HardHat } from "@phosphor-icons/react";
import Header from "../sections/Header";
import Footer from "../sections/Footer";
import ClientsMarquee from "../sections/ClientsMarquee";
import { useSiteContent } from "../i18n/SiteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEHOLDER_PROJECTS = [
  { id: "p1", client: "Enterprise Data Centre", contractor: "Alpha Data",       location: "Dubai, UAE",     industry: "Data Centre", products: ["UPS Systems", "Structured Cabling", "Access Control"], image_id: null, summary: "Delivered a Tier-III aligned power backup and structured cabling stack with 99.99% uptime target." },
  { id: "p2", client: "Regional Hospital Group", contractor: "SIBCA",           location: "Abu Dhabi, UAE", industry: "Healthcare",  products: ["BMS", "Fire-rated Cables", "Video Surveillance"], image_id: null, summary: "Integrated BMS and life-safety cabling across a multi-block campus with zero-downtime cutover." },
  { id: "p3", client: "Industrial Automation Facility", contractor: "Verger Delporte", location: "Manama, Bahrain", industry: "Industrial",  products: ["3-phase UPS", "Instrumentation", "Automation"], image_id: null, summary: "Deployed 500 kVA modular UPS and process instrumentation across two shop-floors." },
  { id: "p4", client: "Luxury Hotel Property",   contractor: "Trojan Holding",  location: "Riyadh, KSA",   industry: "Hospitality", products: ["GRMS", "Lighting Control", "Home Automation"], image_id: null, summary: "GRMS-driven guest experience with dynamic lighting scenes across 320 keys." },
  { id: "p5", client: "Commercial Tower — Downtown", contractor: "Emaar",       location: "Dubai, UAE",    industry: "Commercial",  products: ["Structured Cabling", "Fibre Backbone", "Fire Cables"], image_id: null, summary: "Backbone-to-desktop cabling for a Grade-A commercial tower with 24 floors of tenants." },
  { id: "p6", client: "Solar & Storage Programme", contractor: "Masdar",        location: "Nairobi, Kenya",industry: "Renewables",  products: ["Solar PV", "BESS", "SCADA"],                image_id: null, summary: "Hybrid microgrid programme with monitoring and remote SCADA visibility." },
];

const DEFAULT_INDUSTRIES = ["All", "Commercial", "Healthcare", "Industrial", "Hospitality", "Data Centre", "Renewables"];

function ProjectImage({ imageId, name, index }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!imageId) { setSrc(null); return; }
    let c = false;
    fetch(`${API}/images/${imageId}`).then((r) => r.json()).then((j) => !c && setSrc(j.data_url)).catch(() => {});
    return () => { c = true; };
  }, [imageId]);
  if (src) return <img src={src} alt={name} className="absolute inset-0 w-full h-full object-cover" />;
  const hues = [
    "linear-gradient(135deg, #0F2A6E 0%, #1B6FE0 100%)",
    "linear-gradient(135deg, #0B1F4D 0%, #2E7BFF 100%)",
    "linear-gradient(135deg, #1B6FE0 0%, #0F2A6E 100%)",
    "linear-gradient(135deg, #142857 0%, #4A90FF 100%)",
  ];
  return (
    <div className="absolute inset-0" style={{ background: hues[index % hues.length] }}>
      <div className="absolute inset-0 grid-bg opacity-25" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-heading font-black text-white/15 text-[100px] md:text-[140px] leading-none tracking-tight"
              style={{ letterSpacing: "-0.05em" }}>
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>
    </div>
  );
}

export default function ClientsPortfolio() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const { pg, pgList, loaded: cmsLoaded } = useSiteContent();

  const pf = {
    eyebrow:  pg("portfolio.eyebrow",  "Selected work · 2006–2026"),
    heading:  pg("portfolio.heading",  "Projects delivered across seven markets."),
    sub:      pg("portfolio.sub",      "IT infrastructure, ELV/ICT, power backup, security, BMS, automation, solar and enterprise systems — engineered end-to-end with our partners."),
    stats:    pgList("portfolio.stats", []),
    cta_eyebrow: pg("portfolio.cta_eyebrow", "What's next"),
    cta_heading: pg("portfolio.cta_heading", "Planning a similar project?"),
    cta_body:    pg("portfolio.cta_body",    "Share your scope and we'll help you shortlist products, prepare a BOQ and coordinate delivery across your project timeline."),
    cta_text:    pg("portfolio.cta_text",    "Talk to our team"),
    industries:  pgList("portfolio.industries", DEFAULT_INDUSTRIES),
    count_label: pg("portfolio.count_label", "Showing {shown} of {total}"),
  };
  // Normalise industries list — accept ["All","Commercial"] OR [{label:"…"}]
  const industryLabels = Array.isArray(pf.industries)
    ? pf.industries.map((x) => (typeof x === "string" ? x : (x?.label || ""))).filter(Boolean)
    : DEFAULT_INDUSTRIES;

  useEffect(() => {
    document.title = "Clients Portfolio | OpSys Digital";
    setLoading(true);
    axios.get(`${API}/client-projects`)
      .then((r) => {
        // Use API data if present; otherwise fall back to placeholders (only
        // used on brand-new environments where the DB was never seeded).
        setProjects(Array.isArray(r.data) && r.data.length > 0 ? r.data : PLACEHOLDER_PROJECTS);
      })
      .catch(() => setProjects(PLACEHOLDER_PROJECTS))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => filter === "All" ? projects : projects.filter((p) => (p.industry || "").toLowerCase() === filter.toLowerCase()),
    [filter, projects]
  );

  return (
    <>
      <Header />
      <main className="bg-white">
        {/* HERO — editorial split with big number */}
        <section
          data-testid="portfolio-hero"
          className="relative border-b border-slate-100"
          style={{ background: "linear-gradient(180deg, #FAFBFD 0%, #FFFFFF 100%)" }}
        >
          <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-24 md:pt-32 pb-12 md:pb-16">
            <div className="grid lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <div className="text-[11px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#1B6FE0" }}>
                  {pf.eyebrow}
                </div>
                <h1
                  className="mt-4 font-heading font-bold text-[32px] sm:text-[46px] lg:text-[64px] leading-[1.02] tracking-tight"
                  style={{ color: "#0F2A6E", letterSpacing: "-0.03em" }}
                  data-testid="portfolio-h1"
                >
                  {pf.heading}
                </h1>
                <p className="mt-5 text-[14.5px] md:text-[16.5px] text-[#1C2B3A]/85 leading-[1.7] font-light max-w-2xl">
                  {pf.sub}
                </p>
              </div>
              <div className="lg:col-span-4">
                {(pf.stats || []).length > 0 && (
                  <div className="grid grid-cols-3 gap-6 md:gap-8 pb-2 border-b border-slate-200">
                    {pf.stats.slice(0, 3).map((s, i) => (
                      <PortfolioStat key={i} n={s.value} label={s.label} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* FILTER + BENTO GRID */}
        <section id="projects" className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            {/* Filter chips */}
            <div className="flex flex-wrap items-center gap-2 mb-10 md:mb-14" data-testid="portfolio-filters">
              {industryLabels.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setFilter(cat)}
                  data-testid={`filter-${cat.toLowerCase().replace(/\s+/g,'-')}`}
                  className={`px-4 h-9 rounded-full border text-[12.5px] font-medium tracking-tight transition ${
                    filter === cat
                      ? "text-white border-transparent"
                      : "text-[#0F2A6E] border-slate-200 bg-white hover:border-[#1B6FE0]"
                  }`}
                  style={filter === cat ? { background: "#0F2A6E" } : {}}
                >
                  {cat}
                </button>
              ))}
              <div className="ml-auto text-[12px] text-[#6B7A8D]" data-testid="portfolio-count">
                {(pf.count_label || "Showing {shown} of {total}")
                  .replace("{shown}", filtered.length)
                  .replace("{total}", projects.length)}
              </div>
            </div>

            {/* Bento grid — row 1 has the featured pair (wide + normal), rows 2+ are strict 3-col */}
            {(loading || !cmsLoaded) ? (
              <div className="min-h-[420px] flex flex-col items-center justify-center gap-3 py-16" data-testid="portfolio-loader">
                <div className="w-9 h-9 rounded-full border-[3px] border-slate-200 animate-spin" style={{ borderTopColor: "#1B6FE0" }} />
                <div className="text-[12.5px] text-[#6B7A8D]">Loading projects…</div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="min-h-[280px] flex items-center justify-center text-[#6B7A8D] text-[13.5px]" data-testid="portfolio-empty">
                No projects match this filter.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="portfolio-grid">
                {filtered.map((p, i) => {
                  return (
                  <article
                    key={p.id || i}
                    data-testid={`project-card-${i}`}
                    className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-500"
                    style={{ minHeight: 340 }}
                  >
                    <div className="relative w-full overflow-hidden aspect-[4/3]">
                      <ProjectImage imageId={p.image_id} name={p.client} index={i} />
                      {/* gradient overlay for text legibility */}
                      <div
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        style={{ background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.55) 100%)" }}
                      />
                      {/* category chip — shown only when admin set an industry */}
                      {p.industry && (
                        <div className="absolute top-4 left-4">
                          <span className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-[10.5px] font-semibold uppercase tracking-[0.15em] bg-white/95 backdrop-blur"
                                style={{ color: "#0F2A6E" }}>
                            {p.industry}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="p-6 md:p-7">
                      {p.location && (
                        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#1B6FE0" }}>
                          <MapPin size={11} weight="bold" /> {p.location}
                        </div>
                      )}
                      <h3
                        className="mt-2 font-heading font-bold text-[20px] md:text-[22px] tracking-tight leading-[1.15] transition"
                        style={{ color: "#0F2A6E", letterSpacing: "-0.02em" }}
                      >
                        {p.client}
                      </h3>
                      {p.contractor && (
                        <div className="mt-1.5 flex items-center gap-1.5 text-[12px] text-[#6B7A8D]">
                          <HardHat size={11} weight="duotone" /> {p.contractor}
                        </div>
                      )}
                      {p.summary && (
                        <p className="mt-4 text-[13px] text-[#1C2B3A]/85 leading-[1.75] font-light line-clamp-3">
                          {p.summary}
                        </p>
                      )}
                    </div>
                  </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* CLIENT LOGO MARQUEE */}
        <ClientsMarquee />

        {/* CTA */}
        <section className="py-14 sm:py-20 md:py-24" style={{ background: "#FAFBFD" }}>
          <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
            <div className="text-[11px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#1B6FE0" }}>
              {pf.cta_eyebrow}
            </div>
            <h2 className="mt-4 font-heading font-bold text-[26px] sm:text-[38px] lg:text-[46px] tracking-tight leading-[1.08]"
                style={{ color: "#0F2A6E", letterSpacing: "-0.025em" }}>
              {pf.cta_heading}
            </h2>
            <p className="mt-4 text-[14px] sm:text-[14.5px] text-[#1C2B3A]/85 font-light max-w-2xl mx-auto">
              {pf.cta_body}
            </p>
            <Link to="/#faq" data-testid="portfolio-cta"
                  className="mt-8 inline-flex items-center gap-2 h-11 px-6 rounded-md font-semibold text-white transition hover:brightness-110"
                  style={{ background: "#1B6FE0", fontSize: 13.5 }}>
              {pf.cta_text} <ArrowRight size={13} weight="bold" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function PortfolioStat({ n, label }) {
  return (
    <div>
      <div className="font-heading font-bold text-[26px] md:text-[32px] leading-none tracking-tight"
           style={{ color: "#0F2A6E", letterSpacing: "-0.025em" }}>
        {n}
      </div>
      <div className="mt-2 text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#6B7A8D" }}>
        {label}
      </div>
    </div>
  );
}
